import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, List, Grid, ListItem, Typography, ListItemText, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ItemStatus from 'components/ItemStatus';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { promoCodesActions } from 'state/modules/promoCodes';
import { forceTimezone, formatAmount } from 'utils';
import moment from 'moment';
import { UpdatePromoCodeModal } from 'components/Modals';

const PromoCodeDetails = ({ id }) => {
  const dispatch = useDispatch();
  const [openUpdatePromoCodeModal, setOpenUpdatePromoCodeModal] = useState(false);
  const promoCode = useSelector((state) => state.promoCodes.promoCode);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);

  const fetchPromoCodes = useCallback(
    (id) => {
      dispatch(promoCodesActions.fetchPromoCodeById(id));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchPromoCodes(id);
  }, [fetchPromoCodes, id]);

  /**
   * Parse promo code data
   * @param {object} promoCode Object with promo code data
   */
  const getPromoCodeData = (promoCode = {}) => {
    const promoCodeInfo = [
      {
        label: `${i18n.t(`promo_codes.id`)}:`,
        value: `${promoCode?.code || '-'}`,
      },
      {
        label: `${i18n.t(`promo_codes.credit_amount`)}:`,
        value: `$${formatAmount(promoCode.amount || 0)}`,
      },
      {
        label: `${i18n.t(`promo_codes.start_time`)}:`,
        value: promoCode?.start_time
          ? forceTimezone(moment(promoCode?.start_time)).format(`${localeFormat}`)
          : '-',
      },
      {
        label: `${i18n.t(`promo_codes.expiration_time`)}:`,
        value: promoCode?.expiration_time
          ? forceTimezone(moment(promoCode?.expiration_time)).format(`${localeFormat}`)
          : '-',
      },
      {
        label: `${i18n.t(`common.description`)}:`,
        value: promoCode?.description || '-',
        sx: { width: '100%' },
      },
    ];

    return promoCodeInfo;
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];
  /**
   * Open update promo code modal
   */
  const handleUpdatePromoCodeModal = useCallback(() => {
    setOpenUpdatePromoCodeModal((prevState) => !prevState);
  }, []);

  const TextContainer = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: theme.palette.text.primary,
  }));

  const CustomListStyled = styled(List)`
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 15,
    alignItems: 'flex-start',
  `;

  const CustomListItemStyled = styled(ListItem)(({ theme }) => ({
    padding: '0 4px 8px 0',
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.common.greyLight}`,
    },
  }));

  const CustomListItemTextStyled = styled(ListItemText)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    '& .MuiListItemText-primary': {
      color: theme.palette.text.subtitle,
      fontSize: '13px',
      fontWeight: 500,
    },
    '& .MuiListItemText-secondary': {
      color: theme.palette.common.lightNavy,
      fontSize: '13px',
      wordBreak: 'break-word',
    },
  }));

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'promotional_code',
        type: 'can',
      }}
      title={i18n.t('promo_codes.info')}
      headerActionType="vertical"
    >
      <Box sx={{ padding: 0 }}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item size={8}>
            <Typography variant="h3">
              <TextContainer>ID# {id}</TextContainer>
            </Typography>
          </Grid>
          <Grid item size={4} sx={{ textAlign: 'right' }}>
            <ItemStatus
              i18nKey="common"
              status={promoCode?.is_enabled ? i18n.t('common.enabled') : i18n.t('common.disabled')}
            />
          </Grid>
        </Grid>
        <Can I="update" a="promotional_code">
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item size={12} sx={{ textAlign: 'right' }}>
              <Button
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'capitalize',
                }}
                onClick={handleUpdatePromoCodeModal}
                data-cy="update-info"
                color="primary"
              >
                {i18n.t('common.update_information')}
              </Button>
            </Grid>
          </Grid>
        </Can>

        <CustomListStyled>
          {getPromoCodeData(promoCode).map((val, index) => (
            <CustomListItemStyled key={index}>
              <CustomListItemTextStyled
                primary={val.label}
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    sx={!val.sx ? { textAlign: 'right' } : val.sx}
                  >
                    <Typography variant="body2" component="span" color="textSecondary">
                      {val.value}
                    </Typography>
                  </Box>
                }
              />
            </CustomListItemStyled>
          ))}
        </CustomListStyled>

        {openUpdatePromoCodeModal && (
          <UpdatePromoCodeModal
            open={openUpdatePromoCodeModal}
            id={id}
            handleCancel={handleUpdatePromoCodeModal}
          />
        )}
      </Box>
    </Card>
  );
};

PromoCodeDetails.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PromoCodeDetails;
